import { defineMessages } from 'react-intl.macro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { deriveMapStateFromConfig, TWC } from '@tmapy/beetlejuice-utils';
import type { GeoJSONFeature } from '@tmapy/mapcore';
import { useDispatch, useSelector } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';

import {
  SvgDownload,
  CtaBtn,
  CtaLink,
  DangerAlert,
  InfoAlert,
  Input,
  Link as StyleGuideLink,
  SecondaryBtn,
  WaitingAlert,
  WarningAlert,
  Dialog,
  FormRow,
  FormItem,
  BtnBar,
} from '@tmapy/style-guide';

import './OrderDetail.scss';
import './ApprovalItem.scss';

import type { LogResourceType } from '../../types';
import type {
  NestedOrderUserResponse,
  OrderRegionDetailResponse,
  OrderRetrieveResponse,
} from '../../openapi/types';
import { useOrdersRetrieveResource } from '../../openapi/operations/ordersRetrieve';
import { DocTypeEnum } from '../../openapi/types/DocTypeEnum';
import {
  useAuth,
  useDownloadLink,
  createDownloadLink,
  useSSE,
  SSE_TYPE,
  useHistoryAction,
} from '../../hooks';
import { Link } from '../../link';
import { BoxItem } from '../../app/components/BoxItem';
import { MainContent } from '../../app/components/MainContent';
import { MapWrapper } from '../../app/components/MapWrapper';
import { PageTitle } from '../../app/components/PageTitle';
import { OrderAttachmentsSection } from '../../app/components/OrderAttachmentsSection';
import type {
  OrderAttachmentItem,
  UploadProgressHandler,
} from '../../app/components/OrderAttachmentsSection';
import { IprDocumentItem } from '../../app/components/IprDocumentItem';
import {
  iprDocTypeMessages,
  iprDocumentMessages,
} from '../../app/components/iprDocumentMessages';
import { LogList } from '../../logs/components/LogList';
import { QUERY_STATE } from '../../constants';
import { User } from '../../users/components/User';
import { mapConfig } from '../../areas/mapConfig';
import { MapWithAreas } from '../../areas/components/MapWithAreas';
import { UNMANAGED_AREA_LAYERS } from '../../areas/constants';

import { ORDER_STATE, APPROVAL_STATE, usableItemsForFormType } from '../constants';
import { ordersAttachmentsCreate } from '../../openapi/operations/ordersAttachmentsCreate';
import { ordersAttachmentsDestroy } from '../../openapi/operations/ordersAttachmentsDestroy';
import { ordersTaxableSupplyDatePartialUpdate } from '../../openapi/operations/ordersTaxableSupplyDatePartialUpdate';
import { ordersRejectPartialUpdate } from '../../openapi/operations/ordersRejectPartialUpdate';
import { orderRetryProcessing, orderInterruptProcessing } from '../actions/orderDetail';
import { groupRegionsByType } from '../utils/groupRegionsByType';
import { getOrderDetailAcl } from '../utils/orderDetailAcl';

import { ApprovalItem } from './ApprovalItem';
import { AreasSummary } from './AreasSummary';
import {
  dmvsSourceMessages,
  orderApprovalMessages,
  orderMessages,
  orderTaxableSupplyDateMessages,
  originMessages,
  statusZadateleMessages,
  typesMessages,
} from './orderMessages';
import { OrderBadge } from './OrderBadge';
import { createHandlingFeeRows, PriceSummary } from './PriceSummary';
import { PublishsetSummaryItem } from './PublishsetSummaryItem';
import { RejectionDialog } from './RejectionDialog';

const msg = defineMessages({
  orderQueryStateWaiting: {
    id: 'orderDetail.queryState.waiting',
    defaultMessage: 'Čekejte prosím...',
  },
  orderQueryStateFail: {
    id: 'orderDetail.queryState.fail',
    defaultMessage: 'Načtení žádosti o data selhalo. Zkuste to prosím později.',
  },
  orderStateExpiredAlert: {
    id: 'order.state.expired.alert',
    defaultMessage: 'Data již nejsou dostupná.',
  },
  orderPaidWaitingAlert: {
    id: 'order.paid.waitingForApproval.alert',
    defaultMessage: 'Data budou k dispozici po splacení faktury.',
  },
  orderEdit: {
    id: 'orderDetail.edit',
    defaultMessage: 'Upravit žádost o data',
  },
  orderTitle: {
    id: 'orderDetail.title',
    defaultMessage: '{id, select, empty {Žádost o data} other {Žádost o data č. {id}}}',
  },
  orderCreated: {
    id: 'order.attribute.created',
    defaultMessage: 'Podáno',
  },
  orderPublished: {
    id: 'order.attribute.published',
    defaultMessage: 'K vyzvednutí',
  },
  orderDataStateValidAt: {
    id: 'order.attribute.dataStateValidAt',
    defaultMessage: 'Platnost dat:',
  },
  orderCreator: {
    id: 'order.attribute.creator',
    defaultMessage: 'Odběratel',
  },
  orderOrigin: {
    id: 'order.attribute.origin',
    defaultMessage: 'Původ objednávky',
  },
  orderReason: {
    id: 'order.attribute.reason',
    defaultMessage: 'Důvod žádosti',
  },
  orderNote: {
    id: 'order.attribute.note',
    defaultMessage: 'Poznámka',
  },
  orderType: {
    id: 'order.attribute.type',
    defaultMessage: 'Typ výdeje',
  },
  orderDmvsSource: {
    id: 'order.attribute.dmvs_source',
    defaultMessage: 'Zdroj výdeje',
  },
  orderStatusType: {
    id: 'order.attribute.statusType',
    defaultMessage: 'Status žadatele',
  },
  orderAttachments: {
    id: 'order.attribute.attachments',
    defaultMessage: 'Přílohy',
  },
  orderDownloadBtn: {
    id: 'order.downloadBtn',
    defaultMessage: 'Stáhnout data',
  },
  orderPublishsetsTitle: {
    id: 'order.publishsetsTitle',
    defaultMessage: 'Výdejní sady',
  },
  orderAreasTitle: {
    id: 'order.areasTitle',
    defaultMessage: 'Zájmová území',
  },
  orderApprovals: {
    id: 'order.approvalsTitle',
    defaultMessage: 'Schvalování',
  },
  orderLogs: {
    id: 'order.logssTitle',
    defaultMessage: 'Protokol zpracování žádosti o data',
  },
  orderVydejIsPublicTitle: {
    id: 'orderDetail.isPublicTitle',
    defaultMessage: 'Dostupnost',
  },
  orderVydejIsPublicValue: {
    id: 'orderDetail.isPublicValue',
    defaultMessage: '{is_public, select, true {veřejný} other {neveřejný}}',
  },
  orderVydejSubjekt: {
    id: 'orderDetail.subjekt',
    defaultMessage: 'Subjekt',
  },
  orderIsPublic: {
    id: 'orderDetail.isPublic',
    defaultMessage: 'Dostupnost',
  },
  orderIdPodani: {
    id: 'orderDetail.idPodani',
    defaultMessage: 'ID výdeje',
  },
  orderFromPrimaryDBLabel: {
    id: 'orderDetail.fromPrimaryDB.label',
    defaultMessage: 'Výdej z primární databáze',
  },
  orderFromPrimaryDBValue: {
    id: 'order.fromPrimaryDB.value',
    defaultMessage: '{state, select, true { ano } other { ne }}',
  },
  orderRejectionReason: {
    id: 'orderDetail.rejectionReason',
    defaultMessage: 'Důvod zamítnutí',
  },
  orderRejectFail: {
    id: 'orderDetail.reject.fail',
    defaultMessage:
      'Zamítnutí žádosti se nezdařilo, žádost zůstává ve stavu Chyba při zpracování. Zkuste to prosím znovu.',
  },
});

type DetailProps = {
  orderId: string;
};

const logResourceType: LogResourceType = 'order';

export const OrderDetail: React.FC<DetailProps> = React.memo((props) => {
  useSSE(SSE_TYPE.ORDERS);
  const orderId = parseInt(props.orderId, 10);
  const dispatch = useDispatch();
  const auth = useAuth();

  const mapState = useMemo(() => deriveMapStateFromConfig({ map: mapConfig() } as TWC), []);

  const resource = useOrdersRetrieveResource(orderId ? { id: orderId } : null);
  const data = useMemo(() => {
    return (resource.data ? { ...resource.data } : {}) as OrderRetrieveResponse;
  }, [resource.data]);
  const groupedRegionsByType = groupRegionsByType(data.regions);

  const [isUserDetailDialogVisible, setUserDetailDialogVisibility] = useState(false);
  const [isRejectionDialogVisible, setRejectionDialogVisibility] = useState(false);
  const [rejectQueryState, setRejectQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const formatMessage = useMessage();
  const { customerType } = useSelector((state) => state.app.config);
  const usableItems = usableItemsForFormType[customerType];

  const routeToOrderEdit = useHistoryAction('vydejOrderEdit', { id: orderId });

  const [duzpValue, setDuzpValue] = useState('');
  const [isDuzpSaving, setIsDuzpSaving] = useState(false);
  const [isDuzpError, setIsDuzpError] = useState(false);

  useEffect(() => {
    setDuzpValue(data?.taxable_supply_date ?? '');
    setIsDuzpError(false);
  }, [data?.taxable_supply_date]);

  const handleDuzpChange = useCallback((_e: unknown, eventData: { value: string }) => {
    setDuzpValue(eventData.value);
    setIsDuzpError(false);
  }, []);

  const handleDuzpSave = useCallback(async () => {
    setIsDuzpSaving(true);
    setIsDuzpError(false);
    const result = await ordersTaxableSupplyDatePartialUpdate({
      query: { id: orderId },
      request: { taxable_supply_date: duzpValue || null },
    });
    setIsDuzpSaving(false);
    if (!result.ok) {
      setIsDuzpError(true);
    } else {
      resource.refetch();
    }
  }, [orderId, duzpValue, resource]);

  // TODO BEG - rename ACL to actions
  const acl = useMemo(() => getOrderDetailAcl(auth.hasPermission, data), [auth, data]);

  const showDuzpEditor = usableItems?.includes('ipr_taxable_supply_date') && acl.canEditTaxableSupplyDate;

  // Zamítnutí ze schvalování vypisuje odůvodnění samo v příslušném `ApprovalItem`,
  // tenhle řádek kryje jen zamítnutí ze stavu FAILED.
  const showRejectionItem =
    !!data.rejection_reason &&
    !data.approvals?.some((approval) => approval.state === APPROVAL_STATE.REJECTED);

  const areaFeatures = useMemo(
    () =>
      data.regions?.map((region: OrderRegionDetailResponse) => {
        region.feature.properties['layerId'] = UNMANAGED_AREA_LAYERS.PREDEFINED_AREAS;
        return region.feature;
      }) as GeoJSONFeature[],
    [data.regions],
  );

  const handleUserDetailBtnClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setUserDetailDialogVisibility(true);
  }, []);

  const handleDialogClose = () => {
    setUserDetailDialogVisibility(false);
    setRejectionDialogVisibility(false);
  };

  const handleRejectionConfirm = useCallback(
    async (rejection_reason: string) => {
      setRejectionDialogVisibility(false);
      setRejectQueryState(QUERY_STATE.WAITING);

      const response = await ordersRejectPartialUpdate({
        query: { id: orderId },
        request: { rejection_reason },
      });

      if (!response.error && response.ok) {
        setRejectQueryState(QUERY_STATE.SUCCESS);
        resource.refetch();
      } else {
        setRejectQueryState(QUERY_STATE.FAIL);
      }
    },
    [orderId, resource],
  );

  const handleInterruptProcessingClick = useCallback(() => {
    dispatch(orderInterruptProcessing(orderId));
  }, [dispatch, orderId]);

  const handleRetryProcessingClick = useCallback(() => {
    dispatch(orderRetryProcessing(orderId));
  }, [dispatch, orderId]);

  const downloadIcon = {
    position: 'left' as const,
    element: <SvgDownload />,
  };

  const downloadOrderLink = useDownloadLink(data.url, undefined, 'download_order');

  const handleAttachmentUpload = useCallback(
    (file: File, onUploadProgress: UploadProgressHandler) => {
      const request = new FormData();
      request.set('file', file);
      return ordersAttachmentsCreate({ query: { id: orderId }, request, onUploadProgress });
    },
    [orderId],
  );

  const handleAttachmentDelete = useCallback(
    (attachmentId: OrderAttachmentItem['id']) =>
      ordersAttachmentsDestroy({ query: { id: orderId, attachment_id: `${attachmentId}` } }),
    [orderId],
  );

  const attachments = useMemo(
    () =>
      data.attachments?.map((attachment, idx) => {
        const isLast = idx === data.attachments.length - 1;

        const downloadUrl = `${data.url}attachments/${attachment.id}/`;
        const downloadLink = createDownloadLink(downloadUrl, undefined, 'download_attachment');
        return (
          <React.Fragment key={attachment.url}>
            <StyleGuideLink {...downloadLink} severity='colorful'>
              {attachment.name}
            </StyleGuideLink>
            {isLast ? '' : ', '}
          </React.Fragment>
        );
      }),
    [data.attachments],
  );

  return (
    <MainContent width='full'>
      <article className='sg-u-vs-4'>
        <div className='dmx-orderDetail--head sg-u-vs-2'>
          {resource.queryState === QUERY_STATE.SUCCESS && (
            <div className='sg-a-d-f sg-a-g-2 sg-a-ai-c sg-a-jc-sb sg-a-fw-w'>
              <header className='sg-a-d-f sg-a-ai-c sg-a-g-1 sg-a-fw-w'>
                <PageTitle>
                  <FormattedMessage {...msg.orderTitle} values={{ id: data.id || 'empty' }} />
                </PageTitle>
                {usableItems?.includes('state') && data.state && <OrderBadge state={data.state!} />}
              </header>
              <BtnBar>
                {acl.canDownloadOrder && (
                  <CtaLink {...downloadOrderLink} target='_blank' icon={downloadIcon}>
                    <FormattedMessage {...msg.orderDownloadBtn} />
                  </CtaLink>
                )}
                {acl.canEditOrder && (
                  <SecondaryBtn onClick={routeToOrderEdit}>
                    <FormattedMessage {...msg.orderEdit} />
                  </SecondaryBtn>
                )}
                {acl.canInterruptProcessing && (
                  <SecondaryBtn isDanger onClick={handleInterruptProcessingClick}>
                    <FormattedMessage {...orderMessages.interruptBtn} />
                  </SecondaryBtn>
                )}
                {acl.canRetryProcessing && (
                  <SecondaryBtn onClick={handleRetryProcessingClick}>
                    <FormattedMessage {...orderMessages.reprocessBtn} />
                  </SecondaryBtn>
                )}
                {acl.canReject && rejectQueryState !== QUERY_STATE.WAITING && (
                  <SecondaryBtn isDanger onClick={() => setRejectionDialogVisibility(true)}>
                    <FormattedMessage {...orderApprovalMessages.orderRejectBtn} />
                  </SecondaryBtn>
                )}
              </BtnBar>
            </div>
          )}

          {resource.queryState === QUERY_STATE.WAITING && (
            <WaitingAlert>
              <FormattedMessage {...msg.orderQueryStateWaiting} />
            </WaitingAlert>
          )}

          {resource.queryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...msg.orderQueryStateFail} />
            </DangerAlert>
          )}

          {rejectQueryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...msg.orderRejectFail} />
            </DangerAlert>
          )}
        </div>

        {resource.queryState === QUERY_STATE.SUCCESS && (
          <>
            {data.state === ORDER_STATE.EXPIRED && (
              <DangerAlert>
                <FormattedMessage {...msg.orderStateExpiredAlert} />
              </DangerAlert>
            )}

            {data.publishsets.some((ps) => !!ps.price_list_item) &&
              data.state === ORDER_STATE.WAITING_FOR_APPROVAL && (
              <WarningAlert>
                <FormattedMessage {...msg.orderPaidWaitingAlert} />
              </WarningAlert>
            )}

            {showDuzpEditor && (
              <section className='dmx-orderDetail--duzpEditor sg-u-box sg-a-p-2'>
                {!data.taxable_supply_date && (
                  <div className='sg-a-mb-2'>
                    <InfoAlert>
                      <FormattedMessage {...orderTaxableSupplyDateMessages.infoMissing} />
                    </InfoAlert>
                  </div>
                )}
                <FormRow>
                  <FormItem label={formatMessage(orderTaxableSupplyDateMessages.label)}>
                    <div className='dmx-orderDetail--duzpInputRow'>
                      <Input
                        type='date'
                        name='taxable_supply_date'
                        value={duzpValue}
                        isDisabled={isDuzpSaving}
                        onChange={handleDuzpChange}
                      />
                      <CtaBtn isDisabled={isDuzpSaving} onClick={handleDuzpSave}>
                        <FormattedMessage {...orderTaxableSupplyDateMessages.saveBtn} />
                      </CtaBtn>
                    </div>
                    {isDuzpError && (
                      <DangerAlert>
                        <FormattedMessage {...orderTaxableSupplyDateMessages.saveError} />
                      </DangerAlert>
                    )}
                  </FormItem>
                </FormRow>
              </section>
            )}

            <div className='dmx-orderDetail--cols'>
              <div className='sg-u-vs-4'>
                <div className='sg-u-box sg-a-p-2 sg-a-d-f sg-a-fw-w sg-a-g-2'>
                  <div className='sg-a-f-a sg-u-vs-1'>
                    {usableItems?.includes('created') && (
                      <FormRow>
                        <FormItem label={formatMessage(msg.orderCreated)}>
                          {data.created && new Date(data.created).toLocaleString('cs-CZ')}
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('published') && data.published && (
                      <FormRow>
                        <FormItem label={formatMessage(msg.orderPublished)}>
                          {new Date(data.published).toLocaleString('cs-CZ')}
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('order_data_state_valid_at') &&
                      data.order_data_state_valid_at && (
                        <FormRow>
                          <FormItem label={formatMessage(msg.orderDataStateValidAt)}>
                            {new Date(data.order_data_state_valid_at).toLocaleString('cs-CZ')}
                          </FormItem>
                        </FormRow>
                      )}
                    {usableItems?.includes('ipr_taxable_supply_date') && !showDuzpEditor && data.taxable_supply_date && (
                      <FormRow>
                        <FormItem label={formatMessage(orderTaxableSupplyDateMessages.label)}>
                          {new Date(data.taxable_supply_date).toLocaleDateString('cs-CZ')}
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('creator') && !acl.ordersActionHiddenCreator && (
                      <Creator
                        creator={data.creator}
                        originator={{
                          name: data.originator_name,
                          surname: data.originator_surname,
                          email: data.originator_email,
                        }}
                        handleUserDetailBtnClick={handleUserDetailBtnClick}
                      />
                    )}
                    {usableItems?.includes('origin') &&
                      !acl.ordersActionHiddenCreator &&
                      data.origin && (
                        <FormRow>
                          <FormItem label={formatMessage(msg.orderOrigin)}>
                            {formatMessage.lookup(originMessages, data.origin)}
                          </FormItem>
                        </FormRow>
                      )}
                    {usableItems?.includes('ordering_reason') && data.ordering_reason && (
                      <FormRow>
                        <FormItem label={formatMessage(msg.orderReason)}>
                          {data.ordering_reason}
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('note') && data.note && (
                      <FormRow>
                        <FormItem label={formatMessage(msg.orderNote)}>
                          <span className='sg-a-ws-pw'>{data.note}</span>
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('attachments') && attachments.length > 0 && (
                      <FormRow>
                        <FormItem label={formatMessage(msg.orderAttachments)}>
                          {attachments}
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('selection_type') && (
                      <FormRow>
                        <FormItem label={formatMessage(msg.orderType)}>
                          {formatMessage.lookup(typesMessages, data.selection_type!)}
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('dmvs_id_podani') && (
                      <FormRow>
                        <FormItem label={formatMessage(msg.orderIdPodani)}>
                          {data.dmvs_id_podani}
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('dmvs_source') && (
                      <>
                        <FormRow>
                          <FormItem label={formatMessage(msg.orderDmvsSource)}>
                            {data.dmvs_source
                              ? formatMessage.lookup(dmvsSourceMessages, data.dmvs_source)
                              : formatMessage(orderMessages.dmvsSourceEmpty)}
                          </FormItem>
                        </FormRow>
                        <FormRow>
                          <FormItem label={formatMessage(msg.orderStatusType)}>
                            {data.status_zadatele
                              ? formatMessage.lookup(statusZadateleMessages, data.status_zadatele)
                              : '-'}
                          </FormItem>
                        </FormRow>
                      </>
                    )}
                    {usableItems?.includes('from_primary_db') && (
                      <FormRow>
                        <FormItem label={formatMessage(msg.orderFromPrimaryDBLabel)}>
                          <FormattedMessage
                            {...msg.orderFromPrimaryDBValue}
                            values={{ state: data.from_primary_db }}
                          />
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('is_public') && (
                      <FormRow>
                        <FormItem label={formatMessage(msg.orderVydejIsPublicTitle)}>
                          <FormattedMessage
                            {...msg.orderVydejIsPublicValue}
                            values={{ is_public: data.is_public }}
                          />
                        </FormItem>
                      </FormRow>
                    )}
                    {usableItems?.includes('subjekt') && data.subjekt && (
                      <FormRow>
                        <FormItem label={formatMessage(msg.orderVydejSubjekt)}>
                          {`${data.subjekt.jmeno} ${data.subjekt.id_subjektu ? ` (${data.subjekt.id_subjektu})` : ''
                            }`}
                        </FormItem>
                      </FormRow>
                    )}
                  </div>
                </div>

                {usableItems?.includes('publishsets') && data.publishsets.length > 0 && (
                  <section>
                    <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                      <FormattedMessage {...msg.orderPublishsetsTitle} />
                    </h3>
                    <div className='sg-u-box sg-a-p-2'>
                      <ul className='sg-u-vs-1'>
                        {data.publishsets.map((publishset) => {
                          const formats = publishset.required_formats.map(
                            (format: any) => format.name,
                          );
                          return (
                            <li key={publishset.id}>
                              <PublishsetSummaryItem
                                name={publishset.publishset_name}
                                formats={formats}
                                priceListItemName={publishset.price_list_item_name}
                              />
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </section>
                )}

                {usableItems?.includes('areas') && mapState && (
                  <section>
                    <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                      <FormattedMessage {...msg.orderAreasTitle} />
                    </h3>
                    <div className='sg-u-box sg-a-p-2 sg-u-vs-2'>
                      <AreasSummary
                        cities={groupedRegionsByType['OBEC']}
                        cadasters={groupedRegionsByType['CADASTER']}
                        orps={groupedRegionsByType['ORP']}
                        regions={groupedRegionsByType['KRAJ']}
                        customAreasCount={groupedRegionsByType['CUSTOM']?.length}
                      />
                      <MapWrapper>
                        <MapWithAreas state={mapState} features={areaFeatures} />
                      </MapWrapper>
                    </div>
                  </section>
                )}
              </div>
              <div className='dmx-orderDetail--rightCol sg-u-vs-4'>
                {usableItems?.includes('order_attachments') && (
                  <OrderAttachmentsSection
                    resourceUrl={data.url}
                    attachments={data.attachments}
                    onUpload={acl.canUploadAttachment ? handleAttachmentUpload : undefined}
                    onDelete={acl.canDeleteAttachment ? handleAttachmentDelete : undefined}
                  />
                )}

                {usableItems?.includes('ipr_price_summary') &&
                  (data.publishsets.some((ps) => !!ps.price_list_item) ||
                    !!data.handling_fee_net) && (
                    <PriceSummary
                      rows={[
                        ...data.publishsets
                          .filter((ps) => !!ps.price_list_item)
                          .map((ps) => ({
                            key: ps.id,
                            name: ps.publishset_name,
                            area_ha: ps.area_ha,
                            price_list_item_name: ps.price_list_item_name,
                            vat_rate: ps.vat_rate,
                            price_gross: ps.price_gross,
                          })),
                        ...createHandlingFeeRows(formatMessage, data.handling_fee_net),
                      ]}
                      total_gross={data.total_gross}
                      total_net={data.total_net}
                      total_vat={data.total_vat}
                    />
                  )}

                {usableItems?.includes('ipr_documents') && acl.canDownloadIprDocuments && (() => {
                  const isPaid = data.total_gross && parseFloat(data.total_gross) > 0;
                  const docs: { docType: DocTypeEnum; show: boolean }[] = [
                    {
                      docType: DocTypeEnum.podklad_pro_fakturu,
                      show: !!isPaid,
                    },
                    {
                      docType: DocTypeEnum.danovy_doklad,
                      show: !!isPaid && data.state === ORDER_STATE.PUBLISHED,
                    },
                    {
                      docType: DocTypeEnum.protokol_vydej_dat,
                      show: !isPaid,
                    },
                  ].filter((d) => d.show);

                  if (!docs.length) return null;

                  return (
                    <section>
                      <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                        <FormattedMessage {...iprDocumentMessages.sectionTitle} />
                      </h3>
                      <div className='sg-u-box sg-a-mt-1'>
                        {docs.map(({ docType }) => (
                          <IprDocumentItem
                            key={docType}
                            resourceUrl={data.url}
                            docType={docType}
                            label={<FormattedMessage {...iprDocTypeMessages[docType]} />}
                          />
                        ))}
                      </div>
                    </section>
                  );
                })()}

                {usableItems?.includes('approvals') && (data.approvals.length > 0 || showRejectionItem) && (
                  <section>
                    <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                      <FormattedMessage {...msg.orderApprovals} />
                    </h3>
                    <ul className='sg-u-box sg-a-pr-2 sg-a-o-h'>
                      {showRejectionItem && (
                        <BoxItem
                          stateColorClassName='dmx-approvalItem-rejected'
                          stateTooltip={formatMessage(msg.orderRejectionReason)}
                        >
                          <div className='sg-a-pb-1'>
                            <header className='sg-u-type-bold'>
                              <FormattedMessage {...msg.orderRejectionReason} />
                            </header>
                            <p className='sg-a-mt-1'>
                              <q className='dmx-approvalItem--reason'>{data.rejection_reason}</q>
                            </p>
                          </div>
                        </BoxItem>
                      )}
                      {data.approvals.map((approval) => (
                        <ApprovalItem
                          key={approval.id}
                          {...approval}
                          orderId={data.id}
                          orderState={data.state}
                          rejection_reason={data.rejection_reason}
                          resource={resource}
                        />
                      ))}
                    </ul>
                  </section>
                )}

                {acl.canSeeLog && (
                  <section>
                    <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                      <FormattedMessage {...msg.orderLogs} />
                    </h3>
                    <LogList objectId={orderId} resourceType={logResourceType} />
                  </section>
                )}
              </div>
            </div>
          </>
        )}
      </article>
      {isUserDetailDialogVisible && (
        <Dialog width='800px' onEscKeyDown={handleDialogClose} onOverlayClick={handleDialogClose}>
          <User userId={data.creator?.id} onCloseDialog={handleDialogClose} />
        </Dialog>
      )}
      {isRejectionDialogVisible && (
        <Dialog width='400px' onEscKeyDown={handleDialogClose} onOverlayClick={handleDialogClose}>
          <RejectionDialog
            confirmRejected={handleRejectionConfirm}
            closeRejectionDialog={handleDialogClose}
          />
        </Dialog>
      )}
    </MainContent>
  );
});

type CreatorProps = {
  creator: NestedOrderUserResponse;
  originator: {
    name: OrderRetrieveResponse['originator_name'];
    surname: OrderRetrieveResponse['originator_surname'];
    email: OrderRetrieveResponse['originator_email'];
  };
  handleUserDetailBtnClick: (e: React.MouseEvent) => void;
};

const Creator: React.FC<CreatorProps> = React.memo(
  ({ creator, originator, handleUserDetailBtnClick }) => {
    const formatMessage = useMessage();
    const creatorDetailProps = creator?.id ? { id: creator?.id } : null;
    const creatorFullName = `${creator?.name ?? originator.name ?? ''} ${creator?.surname ?? originator.surname ?? ''
      }`;
    const creatorEmail = `${creator?.email ?? originator.email ?? ''}`;

    return (
      <FormRow>
        <FormItem label={formatMessage(msg.orderCreator)}>
          {creatorDetailProps ? (
            <Link
              to='vydejUserDetail'
              params={creatorDetailProps}
              onClick={handleUserDetailBtnClick}
              severity='colorful'
            >
              {creatorFullName}
            </Link>
          ) : (
            <>{creatorFullName}</>
          )}
          {creator?.account_type ? ` (${creator.account_type})` : ''}
          <div className='sg-u-type-sml'>
            {creator?.company_name && (
              <>
                <span className='sg-u-c-body-darkest'>{creator.company_name}</span>
                {', '}
              </>
            )}
            {creatorEmail && (
              <StyleGuideLink href={`mailto:${creatorEmail}`} severity='colorful'>
                {creatorEmail}
              </StyleGuideLink>
            )}
          </div>
        </FormItem>
      </FormRow>
    );
  },
);
